import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Configuration for lazy loading with retry
 */
interface LazyRetryConfig {
  /** Number of retry attempts before giving up */
  maxRetries?: number;
  /** Delay between retries in milliseconds */
  retryDelay?: number;
  /** Whether to reload the page on persistent failure */
  reloadOnFailure?: boolean;
}

const DEFAULT_CONFIG: Required<LazyRetryConfig> = {
  maxRetries: 3,
  retryDelay: 1000,
  reloadOnFailure: true,
};

/**
 * Storage key for tracking refresh attempts
 */
const REFRESH_KEY = 'lazy_module_refresh_timestamp';
const REFRESH_COOLDOWN = 10000; // 10 seconds

/**
 * Check if we've recently attempted a refresh to avoid infinite refresh loops
 */
function hasRecentlyRefreshed(): boolean {
  const lastRefresh = sessionStorage.getItem(REFRESH_KEY);
  if (!lastRefresh) return false;

  const timeSinceRefresh = Date.now() - parseInt(lastRefresh, 10);
  return timeSinceRefresh < REFRESH_COOLDOWN;
}

/**
 * Mark that we've attempted a refresh
 */
function markRefreshAttempt(): void {
  sessionStorage.setItem(REFRESH_KEY, Date.now().toString());
}

/**
 * Clear the refresh marker after successful load
 */
function clearRefreshMarker(): void {
  sessionStorage.removeItem(REFRESH_KEY);
}

/**
 * Delay execution for a specified time
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if an error is a chunk loading failure
 */
function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('loading chunk') ||
      message.includes('loading css chunk') ||
      message.includes('dynamically imported module') ||
      message.includes('failed to load') ||
      message.includes('error loading')
    );
  }
  return false;
}

/**
 * Creates a lazy component with automatic retry logic for dynamic import failures.
 *
 * This solves the "Failed to fetch dynamically imported module" error that occurs when:
 * - A new deployment changes chunk hashes
 * - The browser/service worker has cached old HTML referencing old chunk names
 * - The old chunks no longer exist on the server
 *
 * The solution:
 * 1. Retry the import with cache-busting query parameter
 * 2. If retries fail, reload the page to get fresh HTML
 * 3. Prevent infinite refresh loops with cooldown tracking
 *
 * @param importFn - The dynamic import function, e.g., () => import('./MyComponent')
 * @param config - Optional retry configuration
 * @returns A lazy component that handles loading failures gracefully
 *
 * @example
 * // Instead of:
 * const MyComponent = lazy(() => import('./MyComponent'));
 *
 * // Use:
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  config: LazyRetryConfig = {}
): LazyExoticComponent<T> {
  const { maxRetries, retryDelay, reloadOnFailure } = { ...DEFAULT_CONFIG, ...config };

  return lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // On retry attempts, add cache-busting query to force fresh fetch
        if (attempt > 0) {
          // Wait before retry
          await delay(retryDelay * attempt);

          // Clear module cache and retry (this works for some bundlers)
          // For Vite, we mainly rely on the page reload as fallback
        }

        const module = await importFn();

        // Success! Clear any refresh markers
        clearRefreshMarker();

        return module;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Only retry for chunk loading errors
        if (!isChunkLoadError(error)) {
          throw error;
        }

        console.warn(
          `[lazyWithRetry] Failed to load module (attempt ${attempt + 1}/${maxRetries + 1}):`,
          lastError.message
        );
      }
    }

    // All retries failed
    console.error('[lazyWithRetry] All retry attempts failed:', lastError);

    // If configured to reload and we haven't recently refreshed, try reloading the page
    if (reloadOnFailure && !hasRecentlyRefreshed()) {
      console.info('[lazyWithRetry] Attempting page reload to get fresh assets...');
      markRefreshAttempt();
      window.location.reload();

      // Return a promise that never resolves to prevent React errors during reload
      return new Promise(() => {});
    }

    // If we've recently refreshed or reload is disabled, throw the error
    // This allows the error boundary to catch and display a user-friendly message
    throw lastError || new Error('Failed to load module after all retry attempts');
  });
}

/**
 * A variant that provides a custom fallback component on persistent failure
 * instead of triggering a page reload.
 */
export function lazyWithFallback<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  FallbackComponent: ComponentType<{ error?: Error; retry: () => void }>,
  config: Omit<LazyRetryConfig, 'reloadOnFailure'> = {}
): LazyExoticComponent<T> {
  return lazyWithRetry(importFn, { ...config, reloadOnFailure: false });
}

export default lazyWithRetry;
